import { useState } from "react";
import type { ReactNode } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  IconButton,
  Input,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  useCreate,
  useDelete,
  useList,
  useNotification,
  useOne,
  useUpdate,
} from "@refinedev/core";
import {
  Activity,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Crosshair,
  Database,
  Eye,
  Gauge,
  Info,
  Landmark,
  Layers3,
  Pause,
  Play,
  Plus,
  Power,
  Search,
  Settings2,
  ShieldCheck,
  Square,
  TestTubeDiagonal,
  Trash2,
  TrendingUp,
  WalletCards,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { DataTable, type Column } from "../components/tables";
import { AppSelect } from "../components/app-select";
import { PopConfirm } from "../components/pop-confirm";
import {
  ExecutionModePills,
  RuntimeActivityPill,
  RuntimeHeartbeatStatus,
  RuntimePulseDot,
  runtimeHeartbeatAge,
} from "../components/runtime-activity";
import {
  DynamicStrategySettingsForm,
  type StrategySettingDefinition,
} from "../components/dynamic-strategy-settings-form";
import { StrategyEntryDiagnostics } from "../components/strategy-entry-diagnostics";
import { CapitalAdvisoryPanel } from "../components/capital-advisory";
import {
  DataBar,
  MetricCard,
  Money,
  PageHeader,
  Panel,
  StatusPill,
} from "../components/ui";
import type { BotRecord, BrokerAccountRecord } from "../types/domain";

export function BotsPage() {
  const [search, setSearch] = useState("");
  const [marketFilter, setMarketFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { result, query } = useList<BotRecord>({
    resource: "bots",
    pagination: { currentPage: 1, pageSize: 10_000 },
    liveMode: "auto",
    queryOptions: { refetchInterval: 2_000 },
  });
  const { mutate: deleteBot } = useDelete();
  const { open } = useNotification();
  const allRows = result.data ?? [];
  const rows = allRows.filter((row) => {
    const matchesSearch = `${row.name} ${row.symbol} ${row.strategy}`
      .toLowerCase()
      .includes(search.toLowerCase());
    return (
      matchesSearch &&
      (!marketFilter || row.market === marketFilter) &&
      (!modeFilter || row.mode === modeFilter) &&
      (!statusFilter || row.status === statusFilter)
    );
  });
  const removeBot = (bot: BotRecord) =>
    new Promise<void>((resolve, reject) => {
      deleteBot(
        { resource: "bots", id: bot.id, successNotification: false, errorNotification: false },
        {
          onSuccess: () => {
            open?.({
              type: "success",
              message: "Bot deleted",
              description: `${bot.name} was removed. Audit history was retained.`,
            });
            void query.refetch();
            resolve();
          },
          onError: (error) => {
            open?.({
              type: "error",
              message: "Could not delete bot",
              description: error.message,
            });
            reject(error);
          },
        },
      );
    });
  const columns: Column<BotRecord>[] = [
    {
      key: "name",
      header: "Bot",
      render: (r) => (
        <Box>
          <Link to={`/bots/${r.id}`} style={{ textDecoration: "none" }}>
            <Text fontWeight="750" _hover={{ color: "brand.400" }}>
              {r.name}
            </Text>
          </Link>
          <Text fontSize="10px" color="fg.muted">
            {r.symbol} · {r.broker}
          </Text>
        </Box>
      ),
    },
    {
      key: "strategy",
      header: "Strategy",
      hideBelow: "lg",
      render: (r) => <Text color="fg.muted">{r.strategy}</Text>,
    },
    {
      key: "mode",
      header: "Mode",
      render: (r) => <ExecutionModePills bot={r} />,
    },
    {
      key: "status",
      header: "Lifecycle",
      render: (r) => (
        <StatusPill
          tone={
            r.status === "RUNNING"
              ? "positive"
              : r.status === "FAULTED"
                ? "negative"
                : "neutral"
          }
        >
          {r.status}
        </StatusPill>
      ),
    },
    {
      key: "runtimeActivity",
      header: "Runtime",
      render: (r) => (
        <HStack gap="2" align="center">
          <RuntimeActivityPill bot={r} compact />
          <RuntimePulseDot bot={r} />
        </HStack>
      ),
    },
    {
      key: "equity",
      header: "Equity",
      numeric: true,
      hideBelow: "md",
      render: (r) => <Money value={r.equity} />,
    },
    {
      key: "pnlToday",
      header: "Today",
      numeric: true,
      render: (r) => <Money value={r.pnlToday} signed />,
    },
    {
      key: "exposure",
      header: "Exposure",
      numeric: true,
      hideBelow: "md",
      render: (r) => <Text fontFamily="mono">{r.exposure.toFixed(2)}x</Text>,
    },
    {
      key: "heartbeat",
      header: "Market data",
      hideBelow: "xl",
      render: (r) => (
        <Text
          color={
            r.marketSession === "OUTSIDE_TRADING_HOURS"
              ? "status.warning"
              : "fg.muted"
          }
        >
          {r.marketSession === "OUTSIDE_TRADING_HOURS" && !r.lastMarketEvent
            ? "Outside trading hours"
            : (r.lastMarketEvent ?? "Waiting for finalized data")}
        </Text>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      numeric: true,
      mobilePriority: "primary",
      render: (r) => {
        const active = ["RUNNING", "STARTING", "RECOVERING"].includes(r.status);
        return (
          <PopConfirm
            title="Delete this bot?"
            description={
              <Stack gap="1">
                <Text color="fg.default" fontWeight="700">
                  {r.name}
                </Text>
                <Text>
                  Configuration and runtime state will be removed. Execution and audit history will be retained.
                </Text>
              </Stack>
            }
            confirmLabel="Delete bot"
            confirmAriaLabel={`Confirm delete bot ${r.name}`}
            cancelAriaLabel={`Cancel delete bot ${r.name}`}
            onConfirm={() => removeBot(r)}
            trigger={
              <IconButton
                aria-label={`Delete ${r.name}`}
                title={
                  active
                    ? "Stop the bot before deleting it"
                    : "Delete bot"
                }
                size="xs"
                variant="ghost"
                color="status.negative"
                disabled={active}
              >
                <Box as={Trash2} boxSize="3.5" />
              </IconButton>
            }
          />
        );
      },
    },
  ];
  return (
    <Stack gap="5">
      <PageHeader
        title="Bots"
        description="Create, operate and inspect cTrader bots."
        actions={
          <Button
            asChild
            size="sm"
            bg="brand.500"
            color="white"
            _hover={{ bg: "brand.600" }}
          >
            <Link to="/bots/create">
              <Box as={Plus} boxSize="4" /> Create bot
            </Link>
          </Button>
        }
      />
      <Panel>
        <Flex gap="3" direction={{ base: "column", md: "row" }}>
          <HStack
            flex="1"
            bg="bg.subtle"
            borderRadius="4px"
            px="3"
            borderWidth="1px"
            borderColor="border.default"
          >
            <Box as={Search} boxSize="4" color="fg.muted" />
            <Input
              variant="subtle"
              border="0"
              bg="transparent"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search bot, symbol or strategy"
              fontSize="sm"
            />
          </HStack>
          <HStack gap="2">
            <FilterSelect
              value={marketFilter}
              onChange={setMarketFilter}
              label="Market"
              options={[...new Set(allRows.map((row) => row.market))]}
            />
            <FilterSelect
              value={modeFilter}
              onChange={setModeFilter}
              label="Mode"
              options={[...new Set(allRows.map((row) => row.mode))]}
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              label="Status"
              options={[...new Set(allRows.map((row) => row.status))]}
            />
          </HStack>
        </Flex>
      </Panel>
      <Panel title={`Bots · ${rows.length}`}>
        <DataTable
          rows={rows}
          columns={columns}
          rowLabel="bots"
          empty={query.isLoading ? "Loading bots…" : "No matching bots"}
        />
      </Panel>
    </Stack>
  );
}

function FilterSelect({
  value,
  onChange,
  label,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  options: string[];
}) {
  return (
    <AppSelect
      ariaLabel={`Filter by ${label.toLowerCase()}`}
      value={value}
      onValueChange={onChange}
      width="auto"
      minW="118px"
      options={[
        { label: `${label}: All`, value: "" },
        ...options.map((option) => ({ label: option, value: option })),
      ]}
    />
  );
}

const steps = [
  {
    label: "Market",
    description: "Select the market supported by the server execution stack.",
    icon: Landmark,
  },
  {
    label: "Instrument",
    description: "Choose the exact instrument from the server registry.",
    icon: Crosshair,
  },
  {
    label: "Broker / Data",
    description: "Confirm the real market-data and execution adapters.",
    icon: Database,
  },
  {
    label: "Strategy",
    description: "Select the production strategy the trading kernel will run.",
    icon: Layers3,
  },
  {
    label: "Strategy Settings",
    description: "Review strategy-owned parameters before creating the bot.",
    icon: Settings2,
  },
  {
    label: "Safety",
    description:
      "Set hard leverage and drawdown boundaries enforced by the server.",
    icon: ShieldCheck,
  },
  {
    label: "Trading Mode",
    description: "Choose durable paper execution or guarded live execution.",
    icon: Activity,
  },
  {
    label: "Review",
    description: "Verify the complete configuration before it is persisted.",
    icon: CircleCheck,
  },
];

const THE5ERS_HIGH_STAKES_DRAWDOWN_GUARD_PCT = 8;
const THE5ERS_HIGH_STAKES_DEFAULT_INITIAL_BALANCE = 100_000;

export function CreateBotPage() {
  const navigate = useNavigate();
  const { open } = useNotification();
  const { mutate: createBot, mutation } = useCreate();
  const brokerAccountsQuery = useList<BrokerAccountRecord>({
    resource: "brokerAccounts",
    pagination: { currentPage: 1, pageSize: 500 },
    liveMode: "off",
  });
  const [step, setStep] = useState(0);
  const [market, setMarket] = useState("Forex");
  const [symbol, setSymbol] = useState("XAUUSD");
  const [broker, setBroker] = useState("cTrader");
  const [strategy, setStrategy] = useState("xau-multi-edge-3-v1");
  const [mode, setMode] = useState("PAPER");
  const [brokerEnvironment, setBrokerEnvironment] = useState("DEMO");
  const [brokerAccountId, setBrokerAccountId] = useState("");
  const [maxLeverage, setMaxLeverage] = useState("18");
  const [maxDrawdown, setMaxDrawdown] = useState("40");
  const the5ersProgram = "high-stakes";
  const [propFirmInitialBalance, setPropFirmInitialBalance] = useState(
    String(THE5ERS_HIGH_STAKES_DEFAULT_INITIAL_BALANCE),
  );
  const normalizedBrokerName = (value?: string) => (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const compatibleBroker = (account: BrokerAccountRecord) => {
    const name = normalizedBrokerName(account.brokerName);
    if (strategy === "xau-multi-edge-3-the5ers-v1") {
      return name.includes("the5ers") || name.includes("5ers") || name.includes("fivepercentonline");
    }
    if (strategy === "xau-multi-edge-3-v1") return name.includes("pepperstone");
    return true;
  };
  const matchingBrokerAccounts = (brokerAccountsQuery.result.data ?? []).filter(
    (account) => account.environment === brokerEnvironment && account.status === "AUTHORIZED" && account.connectionStatus === "AUTHORIZED" && compatibleBroker(account),
  );
  const effectiveBrokerAccountId = matchingBrokerAccounts.some((account) => account.id === brokerAccountId)
    ? brokerAccountId
    : (matchingBrokerAccounts[0]?.id ?? "");

  const submitBot = () => {
    const leverageLimit = Number(maxLeverage);
    const drawdownLimit = Number(maxDrawdown);
    const officialInitialBalance = Number(propFirmInitialBalance);
    const isThe5ers = strategy === "xau-multi-edge-3-the5ers-v1";
    const configuredExposure =
      strategy === "xau-multi-edge-portfolio-v1"
        ? 14
        : isThe5ers ? 3 : 18;
    if (
      ![configuredExposure, leverageLimit, drawdownLimit].every(
        Number.isFinite,
      ) ||
      configuredExposure <= 0 ||
      leverageLimit <= 0 ||
      drawdownLimit <= 0 ||
      drawdownLimit > 100 ||
      configuredExposure > leverageLimit
    ) {
      open?.({
        type: "error",
        message: "Invalid risk configuration",
        description: "Check target exposure, max leverage and max drawdown.",
      });
      return;
    }
    if (isThe5ers && (!Number.isFinite(officialInitialBalance) || officialInitialBalance <= 0)) {
      open?.({
        type: "error",
        message: "Official initial balance is required",
        description: "Enter the starting balance shown by The5ers. Current equity is not a safe substitute after trading has begun.",
      });
      return;
    }
    const id = `${symbol.toLowerCase()}-${Date.now()}`;
    const isLive = mode === "LIVE";
    const isDemoAccount = isLive && brokerEnvironment === "DEMO";
    const executionEnvironment = isLive ? brokerEnvironment.toLowerCase() : "demo";
    if (isLive && !effectiveBrokerAccountId) {
      open?.({
        type: "error",
        message: "Select a broker account",
        description: `No OAuth-authorized cTrader ${brokerEnvironment} account is selected. Connect one on the Brokers page first.`,
      });
      return;
    }
    const strategyConfig =
      strategy === "xau-multi-edge-portfolio-v1"
            ? {
                environment: executionEnvironment,
                allowDemoBrokerOrders: isDemoAccount,
                initialBalance: 3000,
                tradeStartDay: new Date().toISOString().slice(0, 10),
                strategyTimeZone: "Pepperstone/Server",
                momentumAlignedExposure: 4,
                momentumRangeExposure: 1.5,
                momentumCounterExposure: 0.25,
                frequencyMinAbsMomentum: 0.0001,
                frequencyMaxAbsMomentum: 0.00075,
                frequencyTargetLeverage: 7,
                frequencyStopTriggerPct: 0.01,
                frequencyMaxHoldMinutes: 120,
                frequencyAllowLong: true,
                frequencyAllowShort: true,
                maxAggregateExposure: 14,
                brokerEmergencyStopPct: 0.02,
                adoptExistingBrokerState: false,
              }
            : isThe5ers
              ? {
                  environment: executionEnvironment,
                  allowDemoBrokerOrders: isDemoAccount,
                  initialBalance: officialInitialBalance,
                  propFirmInitialBalance: officialInitialBalance,
                  portfolioRevision: "regime-near-v4-regime-trailing",
                  the5ersProgram,
                  the5ersRuleRevision: "the5ers-official-2026-07",
                  // These fail-closed flags are promoted only after OAuth,
                  // exact-feed replay, schedule inspection and news-guard audit.
                  the5ersFeedValidated: false,
                  the5ersNewsGuardValidated: false,
                  tradeStartDay: new Date().toISOString().slice(0, 10),
                  strategyTimeZone: "Pepperstone/Server",
                  // Verified against the authorized The5ers cTrader XAUUSD
                  // symbol. Runtime still compares this with the broker on
                  // every LIVE start and fails closed if the schedule changes.
                  expectedMarketScheduleTimeZone: "Europe/Bucharest",
                  expectedBrokerNameAliases: ["The5ers", "The 5ers", "Five Percent Online"],
                  momentumSignalStartUtc: "07:30",
                  momentumSignalEndUtc: "08:30",
                  momentumEntryUtc: "08:30",
                  momentumExitUtc: "19:30",
                  regimeRuleCount: 20,
                  regimeMinParticipation: 0.4,
                  regimeMinConsensus: 0.7,
                  regimeTargetLeverage: 1,
                  regimeStopTriggerPct: 0.01,
                  regimeTrailingEnabled: true,
                  regimeTrailingActivationPct: 0.02,
                  regimeTrailingDistancePct: 0.01,
                  regimeMinimumFeatureSamples: 384,
                  regimeRejectUnanimousSnapshotOpposition: true,
                  nearMissEnabled: true,
                  nearLongMinAbsMomentum: 0.0003,
                  nearLongMaxAbsMomentum: 0.0005,
                  nearLongExposure: 4 / 3,
                  nearLongStopTriggerPct: 0.0075,
                  nearShortMinAbsMomentum: 0.0003,
                  nearShortMaxAbsMomentum: 0.0005,
                  nearShortExposure: 3,
                  nearShortStopTriggerPct: 0.01,
                  nearShortMinBearishRegimes: 1,
                  lossStreakGovernorEnabled: true,
                  lossStreakGovernorTriggerLosses: 3,
                  lossStreakGovernorScale: 0.25,
                  maxAggregateExposure: 3,
                  brokerEmergencyStopPct: 0.01,
                  adoptExistingBrokerState: false,
                }
              : {
                environment: executionEnvironment,
                allowDemoBrokerOrders: isDemoAccount,
                initialBalance: 3000,
                portfolioRevision: "regime-near-v3-filtered-short",
                tradeStartDay: new Date().toISOString().slice(0, 10),
                // cTrader timestamps are absolute UTC. Pepperstone's server
                // clock is GMT+2/+3 and follows US DST independently of the
                // VPS/container time zone.
                strategyTimeZone: "Pepperstone/Server",
                expectedBrokerName: "Pepperstone",
                expectedMarketScheduleTimeZone: "America/New_York",
                momentumSignalStartUtc: "07:30",
                momentumSignalEndUtc: "08:30",
                momentumEntryUtc: "08:30",
                momentumExitUtc: "19:30",
                regimeRuleCount: 20,
                regimeMinParticipation: 0.4,
                regimeMinConsensus: 0.7,
                regimeTargetLeverage: 5,
                regimeStopTriggerPct: 0.01,
                regimeMinimumFeatureSamples: 384,
                regimeRejectUnanimousSnapshotOpposition: true,
                nearMissEnabled: true,
                nearLongMinAbsMomentum: 0.0003,
                nearLongMaxAbsMomentum: 0.0005,
                nearLongExposure: 7.2,
                nearLongStopTriggerPct: 0.0075,
                nearShortMinAbsMomentum: 0.0003,
                nearShortMaxAbsMomentum: 0.0005,
                nearShortExposure: 18,
                nearShortStopTriggerPct: 0.01,
                nearShortMinBearishRegimes: 1,
                maxAggregateExposure: 18,
                brokerEmergencyStopPct: 0.02,
                adoptExistingBrokerState: false,
              };
    createBot(
      {
        resource: "bots",
        values: {
          id,
          name: `${symbol} ${strategy === "xau-multi-edge-portfolio-v1" ? "Multi-Edge Portfolio" : isThe5ers ? "Multi-Edge 3 The5ers" : "Multi-Edge 3"}`,
          market: "forex",
          mode: mode.toLowerCase(),
          provider: "ctrader",
          strategyId: strategy,
          symbol,
          timeframe: "5m",
          dataAdapterId: "ctrader-market-data",
          executionAdapterId: isLive
            ? `ctrader-${executionEnvironment}-execution`
            : "ctrader-paper-execution",
          brokerAccountId: isLive ? effectiveBrokerAccountId : undefined,
          instrument: {
            market: "forex",
            provider: "ctrader",
            symbol,
            quoteCurrency: "USD",
            quantityUnit: "lot",
            quantityStep: 0.01,
            minQuantity: 0.01,
            contractMultiplier: 100,
            priceTick: 0.01,
            maxLeverage: 30,
            allowFractional: true,
            allowShort: true,
            feeModel: {
              kind: "fixed",
              spreadBps: 1,
              slippageBpsPerSide: 0.2,
              commissionPerUnitPerSide: 3.5,
              minimumCommissionPerSide: 0.04,
            },
          },
          credentialEnv: {
            clientId: "CTRADER_CLIENT_ID",
            clientSecret: "CTRADER_CLIENT_SECRET",
          },
          strategyConfig,
          safety: {
            maxLeverage: leverageLimit,
            maxPositionNotional: 2000000,
            maxOrderNotional:
              strategy === "xau-multi-edge-portfolio-v1"
                ? 2000000
                : isThe5ers ? 2000000 : 3000000,
            notionalLimitMode: "equity_scaled",
            maxPositionNotionalEquityMultiple: leverageLimit,
            maxOrderNotionalEquityMultiple:
              strategy === "xau-multi-edge-3-v1" ? 23 : isThe5ers ? 3.8 : leverageLimit,
            maxDailyLossPct:
              isThe5ers
                ? 0.035
                : strategy === "xau-multi-edge-3-v1" ? 0.12 : 0.08,
            ...(isThe5ers
              ? {
                  riskDayTimeZone: "Etc/GMT-3",
                  dailyLossReference: "day_start_max_balance_equity",
                  absoluteLossInitialBalance: officialInitialBalance,
                  maxAbsoluteLossPct: 0.08,
                }
              : {}),
            ...(strategy === "xau-multi-edge-3-v1"
              ? {
                  maxSessionCloseLossPct: 0.09,
                  sessionCloseTime: "19:30",
                  sessionRiskTimeZone: "Pepperstone/Server",
                }
              : {}),
            maxDrawdownPct: isThe5ers ? THE5ERS_HIGH_STAKES_DRAWDOWN_GUARD_PCT / 100 : drawdownLimit / 100,
            maxOrdersPerDay: isThe5ers ? 10 : 30,
            maxDataAgeMs: 60000,
            maxPriceDeviationBps: 30,
            rejectMinimumQuantityOvershootPct: 0.25,
            pauseOnDataGap: true,
            pauseOnReconcileMismatch: true,
            requireFinalizedCandles: true,
            allowLiveOrders: isLive,
            autoRestart: true,
            maxRestartDelayMs: 60000,
            reconcileIntervalMs: 60000,
          },
          enabled: false,
        },
      },
      {
        onSuccess: () => {
          open?.({
            type: "success",
            message: "cTrader bot created",
            description: "The bot is disabled until you explicitly start it.",
          });
          navigate(`/bots/${id}`);
        },
        onError: (error) =>
          open?.({
            type: "error",
            message: "Could not create bot",
            description: error.message,
          }),
      },
    );
  };

  const content = [
    <WizardMarket market={market} setMarket={setMarket} />,
    <WizardInstrument symbol={symbol} setSymbol={setSymbol} market={market} />,
    <WizardAdapters broker={broker} setBroker={setBroker} />,
    <WizardStrategy
      strategy={strategy}
      setStrategy={(next) => {
        setStrategy(next);
        setMaxLeverage(next === "xau-multi-edge-3-v1" ? "18" : next === "xau-multi-edge-3-the5ers-v1" ? "3" : "14");
        setMaxDrawdown(next === "xau-multi-edge-3-the5ers-v1" ? "4" : "35");
      }}
    />,
    <WizardStrategySettings
      strategy={strategy}
      propFirmInitialBalance={propFirmInitialBalance}
      setPropFirmInitialBalance={setPropFirmInitialBalance}
    />,
    <WizardSafety
      strategy={strategy}
      maxLeverage={maxLeverage}
      setMaxLeverage={setMaxLeverage}
      maxDrawdown={strategy === "xau-multi-edge-3-the5ers-v1" ? String(THE5ERS_HIGH_STAKES_DRAWDOWN_GUARD_PCT) : maxDrawdown}
      setMaxDrawdown={setMaxDrawdown}
    />,
    <WizardMode
      mode={mode}
      setMode={setMode}
      brokerEnvironment={brokerEnvironment}
      setBrokerEnvironment={setBrokerEnvironment}
      accounts={matchingBrokerAccounts}
      brokerAccountId={effectiveBrokerAccountId}
      setBrokerAccountId={setBrokerAccountId}
      accountsLoading={brokerAccountsQuery.query.isLoading}
    />,
    <WizardReview
      market={market}
      symbol={symbol}
      broker={broker}
      strategy={strategy}
      mode={mode}
      brokerEnvironment={brokerEnvironment}
      brokerAccount={(brokerAccountsQuery.result.data ?? []).find((account) => account.id === effectiveBrokerAccountId)}
      targetLeverage={
        strategy === "xau-multi-edge-portfolio-v1"
          ? "14"
          : strategy === "xau-multi-edge-3-the5ers-v1" ? "3" : "18"
      }
      maxLeverage={maxLeverage}
      maxDrawdown={strategy === "xau-multi-edge-3-the5ers-v1" ? String(THE5ERS_HIGH_STAKES_DRAWDOWN_GUARD_PCT) : maxDrawdown}
    />,
  ][step];
  const currentStep = steps[step];
  const progress = ((step + 1) / steps.length) * 100;
  const strategyLabel =
    strategy === "xau-multi-edge-portfolio-v1"
      ? "Multi-Edge Portfolio"
      : strategy === "xau-multi-edge-3-the5ers-v1" ? "Multi-Edge 3 · The5ers" : "Multi-Edge 3";

  return (
    <Stack gap="5">
      <PageHeader
        title="Create Bot"
        description="Configure a cTrader bot with server-validated strategy and safety boundaries."
        back={{ label: "Bots", to: "/bots" }}
        actions={
          <StatusPill tone="info">
            STEP {step + 1} OF {steps.length}
          </StatusPill>
        }
      />
      <Grid
        templateColumns={{
          base: "1fr",
          lg: "240px minmax(0,1fr)",
          xl: "240px minmax(0,1fr) 300px",
        }}
        gap="4"
        alignItems="start"
      >
        <Panel p="3" position={{ lg: "sticky" }} top="84px">
          <Box px="2" pt="1" pb="3">
            <Flex justify="space-between" align="baseline" gap="3">
              <Text fontSize="xs" fontWeight="750">
                Setup progress
              </Text>
              <Text fontSize="10px" color="fg.subtle">
                {Math.round(progress)}%
              </Text>
            </Flex>
            <Box mt="2">
              <DataBar value={progress} max={100} />
            </Box>
          </Box>
          <Stack gap="1">
            {steps.map((entry, index) => {
              const StepIcon = entry.icon;
              const complete = index < step;
              const active = index === step;
              return (
                <Flex
                  as="button"
                  key={entry.label}
                  w="100%"
                  gap="2.5"
                  p="2.5"
                  align="center"
                  textAlign="left"
                  borderRadius="4px"
                  bg={active ? "rgba(99,102,241,.12)" : "transparent"}
                  color={
                    active
                      ? "brand.400"
                      : complete
                        ? "status.positive"
                        : "fg.muted"
                  }
                  cursor="pointer"
                  onClick={() => setStep(index)}
                  _hover={{ bg: active ? "rgba(99,102,241,.15)" : "bg.subtle" }}
                  transition="background .14s ease"
                >
                  <Flex
                    boxSize="28px"
                    flexShrink="0"
                    borderRadius="4px"
                    borderWidth="1px"
                    borderColor={
                      active
                        ? "brand.500"
                        : complete
                          ? "rgba(16,185,129,.35)"
                          : "border.default"
                    }
                    bg={
                      active
                        ? "rgba(99,102,241,.12)"
                        : complete
                          ? "rgba(16,185,129,.08)"
                          : "bg.subtle"
                    }
                    align="center"
                    justify="center"
                  >
                    <Box as={complete ? Check : StepIcon} boxSize="3.5" />
                  </Flex>
                  <Box minW="0">
                    <Text
                      fontSize="xs"
                      fontWeight={active ? "750" : "650"}
                      truncate
                    >
                      {entry.label}
                    </Text>
                    <Text fontSize="9px" color="fg.subtle">
                      {complete
                        ? "Complete"
                        : active
                          ? "In progress"
                          : `Step ${index + 1}`}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
          </Stack>
        </Panel>

        <Panel
          icon={currentStep.icon}
          title={currentStep.label}
          action={
            <Text fontSize="10px" color="fg.subtle" fontFamily="mono">
              {String(step + 1).padStart(2, "0")} /{" "}
              {String(steps.length).padStart(2, "0")}
            </Text>
          }
        >
          <Flex direction="column" minH={{ lg: "320px" }}>
            <Text fontSize="sm" color="fg.muted" mb="5" maxW="720px">
              {currentStep.description}
            </Text>
            <Box flex="1">{content}</Box>
            <Flex
              justify="space-between"
              align="center"
              gap="3"
              mt="6"
              pt="4"
              borderTopWidth="1px"
              borderColor="border.subtle"
            >
              <Button
                variant="ghost"
                disabled={step === 0}
                onClick={() => setStep((x) => Math.max(0, x - 1))}
              >
                <Box as={ChevronLeft} boxSize="4" />
                Back
              </Button>
              <Button
                bg="brand.500"
                color="white"
                _hover={{ bg: "brand.600" }}
                disabled={mutation.isPending}
                onClick={() =>
                  step === steps.length - 1
                    ? submitBot()
                    : setStep((x) => Math.min(steps.length - 1, x + 1))
                }
              >
                {step === steps.length - 1 ? (
                  <>
                    <Box as={Check} boxSize="4" />
                    Create cTrader Bot
                  </>
                ) : (
                  <>
                    Continue
                    <Box as={ChevronRight} boxSize="4" />
                  </>
                )}
              </Button>
            </Flex>
          </Flex>
        </Panel>

        <Box
          gridColumn={{ base: "1", lg: "2", xl: "3" }}
          gridRow={{ xl: "1" }}
          position={{ xl: "sticky" }}
          top="84px"
        >
          <Panel icon={Eye} title="Configuration" p="4">
            <Stack gap="1">
              <Preview icon={Landmark} label="Market" value={market} />
              <Preview icon={Crosshair} label="Instrument" value={symbol} />
              <Preview icon={Database} label="Broker" value={broker} />
              {mode === "LIVE" && (
                <Preview
                  icon={WalletCards}
                  label="Trading account"
                  value={(brokerAccountsQuery.result.data ?? []).find((account) => account.id === effectiveBrokerAccountId)?.account ?? "Not selected"}
                />
              )}
              <Preview icon={Layers3} label="Strategy" value={strategyLabel} />
              <Preview icon={Activity} label="Mode" value={mode} />
              <Preview
                icon={ShieldCheck}
                label="Safety cap"
                value={`${maxLeverage}x · DD ${maxDrawdown}%`}
              />
            </Stack>
            <Flex
              mt="4"
              pt="4"
              borderTopWidth="1px"
              borderColor="border.subtle"
              gap="2.5"
              align="start"
            >
              <Box as={Info} boxSize="3.5" mt="0.5" color="status.info" />
              <Text fontSize="10px" color="fg.muted">
                Nothing is persisted until the final Create action. The server
                validates every value again.
              </Text>
            </Flex>
          </Panel>
        </Box>
      </Grid>
    </Stack>
  );
}

function WizardMarket({
  market,
  setMarket,
}: {
  market: string;
  setMarket: (x: string) => void;
}) {
  const options = [
    {
      n: "Forex",
      d: "cTrader Open API; leverage-aware sizing and broker reconciliation",
    },
  ];
  return (
    <Grid templateColumns="1fr" gap="3">
      {options.map((x) => (
        <Choice
          key={x.n}
          active={market === x.n}
          onClick={() => setMarket(x.n)}
          title={x.n}
          description={x.d}
          icon={Landmark}
          badge="AVAILABLE"
        />
      ))}
    </Grid>
  );
}
function WizardInstrument({
  symbol,
  setSymbol,
  market,
}: {
  symbol: string;
  setSymbol: (x: string) => void;
  market: string;
}) {
  const options = ["XAUUSD"];
  return (
    <Stack gap="3">
      <Text fontSize="xs" color="fg.muted">
        Only instruments exposed by the server registry can be selected.
      </Text>
      <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }} gap="2">
        {options.map((x) => (
          <Choice
            key={x}
            active={symbol === x}
            onClick={() => setSymbol(x)}
            title={x}
            description={`${market} · USD quoted · fractional lots`}
            compact
            icon={Crosshair}
            badge="SUPPORTED"
          />
        ))}
      </Grid>
    </Stack>
  );
}
function WizardAdapters({
  broker,
  setBroker,
}: {
  broker: string;
  setBroker: (x: string) => void;
}) {
  return (
    <Stack gap="4">
      <Choice
        active={broker === "cTrader"}
        onClick={() => setBroker("cTrader")}
        title="cTrader Open API"
        description="Real cTrader market data with durable paper execution or guarded live execution."
        icon={Database}
        badge="CONNECTED"
      />
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
        <FormBox
          label="Market data adapter"
          value={`${broker} Open API stream`}
        />
        <FormBox label="Execution adapter" value="Selected by trading mode" />
      </Grid>
    </Stack>
  );
}
function WizardStrategy({
  strategy,
  setStrategy,
}: {
  strategy: string;
  setStrategy: (value: string) => void;
}) {
  return (
    <Grid templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }} gap="3">
      <Choice
        active={strategy === "xau-multi-edge-portfolio-v1"}
        onClick={() => setStrategy("xau-multi-edge-portfolio-v1")}
        title="XAU Multi-Edge Portfolio"
        description="5m momentum quality tiers plus the Range Session Frequency sleeve."
        icon={Layers3}
        badge="5M + FREQUENCY"
      />
      <Choice
        active={strategy === "xau-multi-edge-3-v1"}
        onClick={() => setStrategy("xau-multi-edge-3-v1")}
        title="XAU Multi-Edge 3"
        description="5m portfolio combining Regime Consensus Session and Near-Miss Range. Momentum and causal P3 structure are observation-only."
        icon={Layers3}
        badge="2 SLEEVES"
      />
      <Choice
        active={strategy === "xau-multi-edge-3-the5ers-v1"}
        onClick={() => setStrategy("xau-multi-edge-3-the5ers-v1")}
        title="XAU Multi-Edge 3 · The5ers"
        description="The same causal signals in a separate prop-firm profile with broker-visible stops, account-wide loss floors and conservative sizing."
        icon={ShieldCheck}
        badge="PROP RULES"
      />
    </Grid>
  );
}
function WizardStrategySettings({
  strategy,
  propFirmInitialBalance,
  setPropFirmInitialBalance,
}: {
  strategy: string;
  propFirmInitialBalance: string;
  setPropFirmInitialBalance: (value: string) => void;
}) {
  if (strategy === "xau-multi-edge-portfolio-v1")
    return (
      <Stack gap="4">
        <Text fontSize="xs" color="fg.muted">
          Multi-Edge consumes finalized 5m candles, builds causal 15m structure
          and combines both sleeves into one broker target.
        </Text>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
          <FormBox label="Momentum tiers" value="4.0x / 1.5x / 0.25x" />
          <FormBox label="Frequency exposure" value="7.0x" />
          <FormBox
            label="Frequency momentum band"
            value="0.01% to 0.075% (exclusive)"
          />
          <FormBox label="Aggregate cap" value="14x" />
          <FormBox label="Frequency hold / stop" value="120m / 1%" />
          <FormBox
            label="Apply behavior"
            value="Editable after creation while stopped"
          />
        </Grid>
      </Stack>
    );
  if (strategy === "xau-multi-edge-3-the5ers-v1")
    return (
      <Stack gap="4">
        <Text fontSize="xs" color="fg.muted" lineHeight="1.7">
          Signal timing remains on the researched Pepperstone/Server clock. Position sizing and account-wide guards are isolated for The5ers. LIVE start stays fail-closed until this OAuth account's XAU feed, schedule and applicable news rule are verified.
        </Text>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
          <FormBox label="The5ers program" value="High Stakes · locked" />
          <FieldInput
            label="Official initial balance"
            value={propFirmInitialBalance}
            onChange={setPropFirmInitialBalance}
            suffix="USD"
            hint="Defaults to the selected 100k profile; it must match the The5ers dashboard, not current equity"
          />
          <FormBox label="Regime / Near LONG / Near SHORT" value="1.00x / 1.333x / 3.00x" />
          <FormBox label="Broker-visible emergency SL" value="1.00% price distance" />
          <FormBox label="Maximum planned stop risk" value="≤ 3.00% equity before gap/slippage" />
          <FormBox label="Loss-streak governor" value="After 3 losses → 25% size" />
          <FormBox label="Feed validation" value="Required on authorized The5ers cTrader M5" />
          <FormBox label="LIVE approval" value="Blocked until rule/feed/schedule guards pass" />
        </Grid>
      </Stack>
    );
  return (
    <Stack gap="4">
      <Text fontSize="xs" color="fg.muted">
        Multi-Edge 3 consumes finalized 5m candles and combines two mutually
        coordinated sleeves into one broker target.
      </Text>
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
        <FormBox label="Regime Consensus" value="K20 · participation 40% · agreement 70%" />
        <FormBox label="Regime target" value="5x" />
        <FormBox label="Near-Miss tiers" value="LONG 7.2x / SHORT 18x" />
        <FormBox label="Near-Miss bands" value="LONG 0.030–0.050% / SHORT 0.030–0.050% + bearish confirmation" />
        <FormBox label="Aggregate cap" value="18x" />
        <FormBox label="Order rebalance cap" value="23x equity" />
        <FormBox label="Market structure" value="Causal M5 → M15/P3" />
      </Grid>
    </Stack>
  );
}
function WizardSafety({
  strategy,
  maxLeverage,
  setMaxLeverage,
  maxDrawdown,
  setMaxDrawdown,
}: {
  strategy: string;
  maxLeverage: string;
  setMaxLeverage: (x: string) => void;
  maxDrawdown: string;
  setMaxDrawdown: (x: string) => void;
}) {
  return (
    <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
      <FieldInput
        label="Max leverage"
        value={maxLeverage}
        onChange={setMaxLeverage}
        suffix="x"
        hint="Hard safety ceiling"
      />
      {strategy === "xau-multi-edge-3-the5ers-v1"
        ? <FormBox label="Internal high-water guard" value={`${THE5ERS_HIGH_STAKES_DRAWDOWN_GUARD_PCT}% · locked`} />
        : <FieldInput
            label="Max drawdown"
            value={maxDrawdown}
            onChange={setMaxDrawdown}
            suffix="%"
            hint="Persistent kill guard"
          />}
      <FormBox
        label="Hard intraday loss"
        value={strategy === "xau-multi-edge-3-the5ers-v1"
          ? "3.5% internal (official 5%)"
          : strategy === "xau-multi-edge-3-v1" ? "12%" : "8%"}
      />
      {strategy === "xau-multi-edge-3-the5ers-v1" && (
        <FormBox
          label="Absolute equity floor"
          value="8% internal (official 10%)"
        />
      )}
      {strategy === "xau-multi-edge-3-v1" && <FormBox label="Session-close loss" value="9% · 19:30 Pepperstone" />}
      <FormBox label="Stale data timeout" value="60,000 ms" />
      <FormBox label="Duplicate order guard" value="Enabled" />
      <FormBox label="Recovery policy" value="Fail closed + reconcile" />
    </Grid>
  );
}
function WizardMode({
  mode,
  setMode,
  brokerEnvironment,
  setBrokerEnvironment,
  accounts,
  brokerAccountId,
  setBrokerAccountId,
  accountsLoading,
}: {
  mode: string;
  setMode: (x: string) => void;
  brokerEnvironment: string;
  setBrokerEnvironment: (x: string) => void;
  accounts: BrokerAccountRecord[];
  brokerAccountId: string;
  setBrokerAccountId: (x: string) => void;
  accountsLoading: boolean;
}) {
  return (
    <Stack gap="4">
      <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }} gap="3">
        <Choice
          active={mode === "PAPER"}
          onClick={() => setMode("PAPER")}
          title="Paper mode"
          description="Broker market data with orders filled only by the bot's durable simulator."
          icon={TestTubeDiagonal}
          badge="SIMULATED ORDERS"
        />
        <Choice
          active={mode === "LIVE"}
          onClick={() => setMode("LIVE")}
          title="Live mode"
          description="Orders are sent to the selected cTrader broker account environment."
          icon={Activity}
          badge="BROKER ORDERS"
        />
      </Grid>
      {mode === "LIVE" && (
        <Box p="4" borderWidth="1px" borderColor="border.default" borderRadius="4px" bg="bg.subtle">
          <Text fontSize="xs" fontWeight="750">Broker account environment</Text>
          <Text fontSize="10px" color="fg.muted" mt="1" mb="3">
            Both choices use LIVE mode because both send real orders to cTrader. DEMO uses virtual broker funds; LIVE uses real funds.
          </Text>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }} gap="3">
            <Choice
              active={brokerEnvironment === "DEMO"}
              onClick={() => setBrokerEnvironment("DEMO")}
              title="Demo account"
              description="Real cTrader API orders using virtual broker funds."
              icon={TestTubeDiagonal}
              badge="LIVE · DEMO"
              compact
            />
            <Choice
              active={brokerEnvironment === "LIVE"}
              onClick={() => setBrokerEnvironment("LIVE")}
              title="Live account"
              description="Real cTrader API orders using real broker funds."
              icon={Activity}
              badge="REAL FUNDS"
              compact
            />
          </Grid>
          <Box mt="4">
            <Text fontSize="xs" fontWeight="750">Trading account</Text>
            <Text fontSize="10px" color="fg.muted" mt="1" mb="2.5">
              Each bot is permanently bound to this account until it is stopped and explicitly reassigned.
            </Text>
            <AppSelect
              ariaLabel="Select cTrader trading account"
              value={brokerAccountId}
              onValueChange={setBrokerAccountId}
              disabled={accountsLoading || accounts.length === 0}
              invalid={!accountsLoading && accounts.length === 0}
              placeholder={accountsLoading ? "Loading authorized accounts..." : "Select an OAuth-authorized account"}
              options={accounts.map((account) => ({
                value: account.id,
                label: `${account.connectionName ?? "cTrader"} · ${account.account} · ${account.environment}${account.equity === undefined ? "" : ` · $${account.equity.toLocaleString()}`}`,
              }))}
            />
            {!accountsLoading && accounts.length === 0 && (
              <Text mt="2" fontSize="10px" color="status.negative">
                No {brokerEnvironment} account is authorized. Add or reconnect an account under Infrastructure → Brokers.
              </Text>
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
function WizardReview(props: {
  market: string;
  symbol: string;
  broker: string;
  strategy: string;
  mode: string;
  brokerEnvironment: string;
  brokerAccount?: BrokerAccountRecord;
  targetLeverage: string;
  maxLeverage: string;
  maxDrawdown: string;
}) {
  const riskConflict = Number(props.targetLeverage) > Number(props.maxLeverage);
  return (
    <Stack gap="3">
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="2">
        <FormBox
          label="Market / instrument"
          value={`${props.market} · ${props.symbol}`}
        />
        <FormBox
          label="Broker / mode"
          value={props.mode === "LIVE"
            ? `${props.broker} · LIVE · ${props.brokerEnvironment} ACCOUNT`
            : `${props.broker} · PAPER · SIMULATED ORDERS`}
        />
        {props.mode === "LIVE" && (
          <FormBox
            label="Trading account"
            value={props.brokerAccount
              ? `${props.brokerAccount.connectionName ?? "cTrader"} · ${props.brokerAccount.account}`
              : "NOT SELECTED"}
          />
        )}
        <FormBox label="Strategy" value={props.strategy} />
        <FormBox
          label="Target / max exposure"
          value={`${props.targetLeverage}x / ${props.maxLeverage}x`}
        />
      </Grid>
      <Box
        p="3"
        borderRadius="4px"
        bg={riskConflict ? "rgba(244,63,94,.1)" : "rgba(16,185,129,.1)"}
        borderWidth="1px"
        borderColor={
          riskConflict ? "rgba(244,63,94,.28)" : "rgba(16,185,129,.24)"
        }
      >
        <Text
          fontSize="xs"
          fontWeight="700"
          color={riskConflict ? "status.negative" : "status.positive"}
        >
          {riskConflict
            ? "Blocking validation conflict"
            : "Configuration passes UI preflight"}
        </Text>
        <Text fontSize="10px" color="fg.muted" mt="1">
          {riskConflict
            ? "Strategy target exposure exceeds the bot safety max leverage."
            : `Max DD guard ${props.maxDrawdown}%. Backend performs final safety validation.`}
        </Text>
      </Box>
    </Stack>
  );
}
function Choice({
  active,
  onClick,
  title,
  description,
  compact,
  icon: Icon = Layers3,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  description: string;
  compact?: boolean;
  icon?: LucideIcon;
  badge?: string;
}) {
  return (
    <Flex
      as="button"
      w="100%"
      p={compact ? "3" : "4"}
      gap="3"
      align="start"
      textAlign="left"
      borderWidth="1px"
      borderColor={active ? "brand.500" : "border.default"}
      bg={active ? "rgba(99,102,241,.08)" : "bg.subtle"}
      borderRadius="4px"
      cursor="pointer"
      onClick={onClick}
      _hover={{
        borderColor: active ? "brand.500" : "rgba(99,102,241,.45)",
        bg: active ? "rgba(99,102,241,.11)" : "bg.elevated",
      }}
      transition="background .14s ease, border-color .14s ease"
    >
      <Flex
        boxSize={compact ? "32px" : "38px"}
        flexShrink="0"
        align="center"
        justify="center"
        borderRadius="4px"
        bg={active ? "rgba(99,102,241,.16)" : "bg.elevated"}
        color={active ? "brand.400" : "fg.muted"}
      >
        <Box as={Icon} boxSize={compact ? "4" : "4.5"} />
      </Flex>
      <Box flex="1" minW="0">
        <Flex align="center" gap="2" justify="space-between">
          <Text fontWeight="750" fontSize="sm">
            {title}
          </Text>
          {badge && (
            <Text
              fontSize="9px"
              fontWeight="800"
              color={active ? "brand.400" : "fg.subtle"}
              letterSpacing=".04em"
            >
              {badge}
            </Text>
          )}
        </Flex>
        <Text color="fg.muted" fontSize="10px" mt="1.5" lineHeight="1.5">
          {description}
        </Text>
      </Box>
      <Flex
        boxSize="20px"
        flexShrink="0"
        mt="0.5"
        align="center"
        justify="center"
        borderRadius="999px"
        borderWidth="1px"
        borderColor={active ? "brand.500" : "border.default"}
        bg={active ? "brand.500" : "transparent"}
        color="white"
      >
        {active && <Box as={Check} boxSize="3" />}
      </Flex>
    </Flex>
  );
}
function FormBox({ label, value }: { label: string; value: string }) {
  return (
    <Box bg="bg.subtle" borderRadius="4px" p="3">
      <Text fontSize="10px" color="fg.muted">
        {label}
      </Text>
      <Text fontSize="xs" fontWeight="650" mt="1">
        {value}
      </Text>
    </Box>
  );
}
function FieldInput({
  label,
  value,
  onChange,
  suffix,
  hint,
}: {
  label: string;
  value: string;
  onChange: (x: string) => void;
  suffix: string;
  hint: string;
}) {
  return (
    <Box>
      <Text fontSize="10px" color="fg.muted" mb="1.5">
        {label}
      </Text>
      <Flex
        align="center"
        borderWidth="1px"
        borderColor="border.default"
        bg="bg.subtle"
        borderRadius="4px"
        px="3"
      >
        <Input
          border="0"
          bg="transparent"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <Text fontFamily="mono" fontSize="xs">
          {suffix}
        </Text>
      </Flex>
      <Text fontSize="9px" color="fg.subtle" mt="1">
        {hint}
      </Text>
    </Box>
  );
}
function Preview({
  label,
  value,
  icon: Icon = Info,
}: {
  label: string;
  value: string;
  icon?: LucideIcon;
}) {
  return (
    <Flex
      borderRadius="4px"
      p="2.5"
      gap="2.5"
      align="center"
      _hover={{ bg: "bg.subtle" }}
    >
      <Flex
        boxSize="28px"
        flexShrink="0"
        borderRadius="4px"
        bg="bg.subtle"
        color="fg.muted"
        align="center"
        justify="center"
      >
        <Box as={Icon} boxSize="3.5" />
      </Flex>
      <Box minW="0">
        <Text
          fontSize="9px"
          color="fg.subtle"
          textTransform="uppercase"
          letterSpacing=".05em"
        >
          {label}
        </Text>
        <Text fontSize="xs" fontWeight="700" mt="0.5" truncate>
          {value}
        </Text>
      </Box>
    </Flex>
  );
}

export function BotDetailPage() {
  const { id = "xau-session-momentum-ctrader-paper" } = useParams();
  const navigate = useNavigate();
  const { result: bot, query } = useOne<BotRecord>({
    resource: "bots",
    id,
    liveMode: "auto",
    queryOptions: { refetchInterval: 2_000 },
  });
  const { mutate, mutation } = useUpdate<BotRecord>();
  const { mutate: deleteBot } = useDelete();
  const strategies =
    useList<any>({
      resource: "strategies",
      pagination: { currentPage: 1, pageSize: 50 },
    }).result.data ?? [];
  const brokerAccountsQuery = useList<BrokerAccountRecord>({
    resource: "brokerAccounts",
    pagination: { currentPage: 1, pageSize: 500 },
    liveMode: "off",
    queryOptions: { refetchInterval: 2_000 },
  });
  const brokerAccounts = brokerAccountsQuery.result.data ?? [];
  const { open } = useNotification();
  const [pendingAction, setPendingAction] = useState<string>();
  const [pendingBrokerAccountId, setPendingBrokerAccountId] = useState("");
  if (!bot)
    return (
      <Panel>
        <Text color="fg.muted">
          {query.isLoading ? "Loading bot…" : "Bot not found"}
        </Text>
      </Panel>
    );
  const currentEquity = bot.runtime?.lastKnownAccount?.equity ?? bot.equity;
  const highWaterEquity = bot.runtime?.highWaterEquity;
  const currentDrawdownPct = currentEquity !== undefined && highWaterEquity !== undefined && highWaterEquity > 0
    ? Math.max(0, (highWaterEquity - currentEquity) / highWaterEquity * 100)
    : 0;
  const dayStartEquity = bot.runtime?.dayStartEquity;
  const currentDailyLossPct = bot.pnlToday < 0
    ? -bot.pnlToday / Math.max(1, dayStartEquity ?? ((currentEquity ?? 0) - bot.pnlToday)) * 100
    : 0;
  const action = (status: BotRecord["status"], label: string) => {
    setPendingAction(label);
    mutate(
      { resource: "bots", id: bot.id, values: { status }, successNotification: false, errorNotification: false },
      {
        onSuccess: () =>
          open?.({
            type: "success",
            message: `${label} completed`,
            description: `${bot.name} → ${status}`,
          }),
        onError: (error) =>
          open?.({
            type: "error",
            message: `${label} failed`,
            description: error.message,
          }),
        onSettled: () => setPendingAction(undefined),
      },
    );
  };
  const active = ["RUNNING", "STARTING", "RECOVERING"].includes(bot.status);
  const canStop = bot.status !== "STOPPED";
  const paperMode = bot.mode === "PAPER";
  const remove = () =>
    new Promise<void>((resolve, reject) => {
      deleteBot(
        { resource: "bots", id: bot.id, successNotification: false, errorNotification: false },
        {
          onSuccess: () => {
            open?.({
              type: "success",
              message: "Bot deleted",
              description: "Execution and audit history were retained.",
            });
            navigate("/bots");
            resolve();
          },
          onError: (error) => {
            open?.({
              type: "error",
              message: "Could not delete bot",
              description: error.message,
            });
            reject(error);
          },
        },
      );
    });
  const registeredStrategy = strategies.find(
    (item) => item.id === bot.strategy,
  );
  const selectedBrokerAccount = brokerAccounts.find((account) => account.id === bot.brokerAccountId);
  const editableBrokerAccountId = pendingBrokerAccountId || bot.brokerAccountId || "";
  const compatibleBrokerAccounts = brokerAccounts.filter(
    (account) => account.environment === bot.brokerEnvironment && account.status === "AUTHORIZED" && account.connectionStatus === "AUTHORIZED",
  );
  const definitions = (registeredStrategy?.settings ??
    []) as StrategySettingDefinition[];
  return (
    <Stack gap="5">
      <PageHeader
        title={bot.name}
        description={`${bot.symbol} · ${bot.market} · ${bot.broker}`}
        back={{ label: "Bots", to: "/bots" }}
        actions={
          <>
            <StatusPill
              tone={bot.status === "RUNNING" ? "positive" : "neutral"}
            >
              {bot.status}
            </StatusPill>
            <RuntimeActivityPill bot={bot} />
            <ExecutionModePills bot={bot} />
            {bot.mode === "LIVE" && (
              <StatusPill tone={selectedBrokerAccount?.isLive ? "negative" : "warning"}>
                {selectedBrokerAccount
                  ? `${selectedBrokerAccount.connectionName ?? "cTrader"} · ${selectedBrokerAccount.account}`
                  : "ACCOUNT UNBOUND"}
              </StatusPill>
            )}
            {bot.mode === "LIVE" && (
              <StatusPill tone={!active ? "neutral" : bot.accountSnapshotFresh ? "positive" : "negative"}>
                {!active ? "LAST ACCOUNT SNAPSHOT" : bot.accountSnapshotFresh ? "ACCOUNT DATA CURRENT" : "ACCOUNT DATA STALE"}
              </StatusPill>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => action("PAUSED", "Pause")}
              disabled={mutation.isPending || bot.status !== "RUNNING"}
              title="Pause bot and flatten paper exposure"
            >
              {pendingAction === "Pause" ? <Spinner size="xs" /> : <Box as={Pause} boxSize="4" />}
              {pendingAction === "Pause" ? "Pausing..." : "Pause"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => action("STOPPED", "Stop")}
              disabled={mutation.isPending || !canStop}
              title="Stop bot and disable automatic restart"
            >
              {pendingAction === "Stop" ? <Spinner size="xs" /> : <Box as={Square} boxSize="3.5" />}
              {pendingAction === "Stop" ? "Stopping..." : "Stop"}
            </Button>
            <PopConfirm
              title="Delete this bot?"
              description={<Stack gap="1"><Text color="fg.default" fontWeight="700">{bot.name}</Text><Text>Configuration and runtime state will be removed. Execution and audit history will be retained.</Text></Stack>}
              confirmLabel="Delete bot"
              confirmAriaLabel={`Confirm delete bot ${bot.name}`}
              cancelAriaLabel={`Cancel delete bot ${bot.name}`}
              onConfirm={remove}
              trigger={
                <Button
                  size="sm"
                  variant="outline"
                  color="status.negative"
                  disabled={active}
                  title={active ? "Stop the bot before deleting it" : "Delete bot"}
                >
                  <Box as={Trash2} boxSize="3.5" />
                  Delete
                </Button>
              }
            />
          </>
        }
      />
      <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
        <MetricCard
          icon={WalletCards}
          label={paperMode ? "Paper equity" : "Account equity"}
          value={`$${bot.equity.toLocaleString()}`}
          detail={
            paperMode
              ? "Simulated ledger · not broker balance"
              : bot.accountSnapshotAsOf
                ? `cTrader snapshot · ${runtimeHeartbeatAge(bot.accountSnapshotAsOf)}`
                : "Waiting for the first cTrader account snapshot"
          }
        />
        <MetricCard
          icon={TrendingUp}
          label={paperMode ? "Paper PnL today" : "Account PnL today"}
          value={<Money value={bot.pnlToday} signed />}
          positive={bot.pnlToday >= 0}
          change="persisted"
        />
        <MetricCard
          icon={Gauge}
          label={paperMode ? "Paper exposure" : "Account exposure"}
          value={`${bot.exposure.toFixed(2)}x`}
          detail={
            paperMode
              ? "Simulated execution account"
              : "All positions in the bot account"
          }
        />
        <MetricCard
          icon={Crosshair}
          label="Position"
          value={bot.position}
          detail={bot.lastSignal}
        />
      </Grid>
      <Grid
        templateColumns={{
          base: "1fr",
          xl: "minmax(0,1.3fr) minmax(360px,.7fr)",
        }}
        gap="4"
      >
        <Panel icon={Database} title="Strategy entry diagnostics">
          <StrategyEntryDiagnostics bot={bot} />
        </Panel>
        <Stack gap="4">
          <Panel icon={Activity} title="Runtime">
            <Stack gap="3">
              <Timeline
                time={<RuntimeHeartbeatStatus bot={bot} />}
                title={
                  bot.runtimeActivity === "TRACKING"
                    ? "Runtime is tracking market data"
                    : bot.runtimeActivity === "WAITING_FOR_MARKET"
                      ? "Runtime is tracking and waiting for XAUUSD"
                      : "Runtime operation"
                }
                detail={
                  bot.runtimeActivityDetail ??
                  "Waiting for the server to publish runtime activity."
                }
              />
              <Timeline
                time={bot.desiredStatus ?? bot.status}
                title="Desired lifecycle"
                detail={
                  bot.desiredStatus === "RUNNING"
                    ? "Persists across server restarts; the runtime waits through closed sessions and resumes on new candles."
                    : bot.desiredStatus === "PAUSED"
                      ? "Requires an explicit Resume after the account is verified flat."
                      : "Will remain stopped after a server restart."
                }
              />
              <Timeline
                time={
                  bot.lastMarketEvent ??
                  (bot.marketSession === "OUTSIDE_TRADING_HOURS"
                    ? "MARKET CLOSED"
                    : "WAITING")
                }
                title={
                  bot.marketSession === "OUTSIDE_TRADING_HOURS"
                    ? "Market outside trading hours"
                    : "Last finalized market event"
                }
                detail={bot.marketSessionDetail ?? bot.lastSignal}
              />
              {bot.lastStrategyEvaluationAt && (
                <Timeline
                  time={bot.lastStrategyEvaluationAt}
                  title="Last strategy evaluation"
                  detail="A market event reached the kernel and the strategy transition completed."
                />
              )}
              {bot.lastReconcileAt && (
                <Timeline
                  time={bot.lastReconcileAt}
                  title="Last broker reconciliation"
                  detail="Runtime and broker account state were compared successfully."
                />
              )}
              {bot.mode === "LIVE" && (
                <Timeline
                  time={bot.accountSnapshotAsOf ?? "WAITING"}
                  title={bot.accountSnapshotFresh ? "Broker account snapshot is current" : "Broker account snapshot is stale"}
                  detail={bot.accountSnapshotAsOf
                    ? `${runtimeHeartbeatAge(bot.accountSnapshotAsOf)}. Balance, equity, margin, buying power, positions and orders refresh independently of bot restarts.`
                    : "No authoritative cTrader account snapshot has been persisted yet."}
                />
              )}
              {bot.lastExecutionEvent && (
                <Timeline
                  time={bot.lastExecutionEvent}
                  title="Last broker execution event"
                  detail={bot.lastSignal}
                />
              )}{" "}
              {(bot as any).lastError && (
                <Timeline
                  time="ERROR"
                  title="Last runtime error"
                  detail={(bot as any).lastError}
                />
              )}
            </Stack>
          </Panel>
          <Panel icon={ShieldCheck} title="Safety limits">
            <Stack gap="4">
              <Limit
                label="Gross exposure"
                value={bot.exposure}
                max={(bot as any).safety?.maxLeverage ?? 1}
                suffix="x"
              />
              <NotionalLimit
                label="Position notional"
                effective={bot.notionalLimits?.position}
                hard={bot.notionalLimits?.hardPosition ?? bot.safety?.maxPositionNotional}
                multiple={bot.notionalLimits?.positionEquityMultiple}
                mode={bot.notionalLimits?.mode ?? bot.safety?.notionalLimitMode ?? "fixed"}
              />
              <NotionalLimit
                label="Order notional"
                effective={bot.notionalLimits?.order}
                hard={bot.notionalLimits?.hardOrder ?? bot.safety?.maxOrderNotional}
                multiple={bot.notionalLimits?.orderEquityMultiple}
                mode={bot.notionalLimits?.mode ?? bot.safety?.notionalLimitMode ?? "fixed"}
              />
              <Limit
                label="Max drawdown"
                value={currentDrawdownPct}
                max={((bot as any).safety?.maxDrawdownPct ?? 0) * 100 || 1}
                suffix="%"
              />
              <Limit
                label="Hard intraday loss"
                value={currentDailyLossPct}
                max={((bot as any).safety?.maxDailyLossPct ?? 0) * 100 || 1}
                suffix="%"
              />
              {(bot as any).safety?.maxSessionCloseLossPct !== undefined && (
                <Limit
                  label="Session-close loss"
                  value={currentDailyLossPct}
                  max={((bot as any).safety.maxSessionCloseLossPct ?? 0) * 100 || 1}
                  suffix="%"
                />
              )}
            </Stack>
          </Panel>
        </Stack>
      </Grid>
      {bot.mode === "LIVE" && (
        <Panel
          icon={WalletCards}
          title="Broker account binding"
          action={<StatusPill tone={active ? "warning" : "info"}>{active ? "STOP TO CHANGE" : "EDITABLE"}</StatusPill>}
        >
          <Text fontSize="xs" color="fg.muted" mb="3">
            This bot sends orders only to the selected OAuth-authorized account. Changing it never moves positions between accounts.
          </Text>
          <Flex gap="2.5" direction={{ base: "column", md: "row" }} align={{ md: "center" }}>
            <AppSelect
              ariaLabel="Broker account for this bot"
              value={editableBrokerAccountId}
              onValueChange={setPendingBrokerAccountId}
              disabled={active || mutation.isPending}
              invalid={!editableBrokerAccountId}
              options={compatibleBrokerAccounts.map((account) => ({
                value: account.id,
                label: `${account.connectionName ?? "cTrader"} · ${account.account}${account.equity === undefined ? "" : ` · $${account.equity.toLocaleString()}`}`,
              }))}
            />
            <Button
              minW="150px"
              variant="outline"
              disabled={active || mutation.isPending || !pendingBrokerAccountId || pendingBrokerAccountId === bot.brokerAccountId}
              onClick={() => mutate(
                { resource: "bots", id: bot.id, values: { brokerAccountId: pendingBrokerAccountId } as any },
                {
                  onSuccess: () => {
                    setPendingBrokerAccountId("");
                    open?.({ type: "success", message: "Broker account updated", description: "The new binding applies on the next start." });
                  },
                  onError: (error) => open?.({ type: "error", message: "Account change rejected", description: error.message }),
                },
              )}
            >
              Save account
            </Button>
          </Flex>
        </Panel>
      )}
      {bot.mode === "LIVE" && bot.brokerAccountId && <CapitalAdvisoryPanel key={bot.brokerAccountId} accountId={bot.brokerAccountId} />}
      <Panel
        icon={Settings2}
        title="Strategy settings"
        action={
          <StatusPill tone={bot.status === "RUNNING" ? "warning" : "info"}>
            {bot.status === "RUNNING" ? "LOCKED" : "EDITABLE"}
          </StatusPill>
        }
      >
        {definitions.length > 0 ? (
          <DynamicStrategySettingsForm
            definitions={definitions}
            locked={["RUNNING", "STARTING", "RECOVERING"].includes(bot.status)}
            values={(bot as any).strategyConfig ?? {}}
            onSubmit={(values) =>
              mutate(
                {
                  resource: "bots",
                  id: bot.id,
                  values: { strategyConfig: values },
                },
                {
                  onSuccess: () =>
                    open?.({
                      type: "success",
                      message: "Strategy settings saved",
                      description:
                        "The server validated and persisted the settings.",
                    }),
                  onError: (error) =>
                    open?.({
                      type: "error",
                      message: "Settings rejected",
                      description: error.message,
                    }),
                },
              )
            }
          />
        ) : (
          <Text color="fg.muted" fontSize="xs">
            This strategy exposes no editable settings.
          </Text>
        )}
      </Panel>
      <Panel icon={Power} title="Runtime controls">
        <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }} gap="3">
          <Button
            variant="outline"
            onClick={() => action("RUNNING", "Start")}
            disabled={mutation.isPending || bot.status === "RUNNING"}
            title="Start or resume this bot"
          >
            {pendingAction === "Start" ? <Spinner size="xs" /> : <Box as={Play} boxSize="4" />}
            {pendingAction === "Start" ? "Starting..." : "Start / Resume"}
          </Button>
          <Button asChild variant="outline">
            <Link
              to={`/research/backtests/create?strategy=${encodeURIComponent(bot.strategy)}&botId=${encodeURIComponent(bot.id)}`}
            >
              <Box as={TestTubeDiagonal} boxSize="4" />
              Run Backtest
            </Link>
          </Button>
        </Grid>
        <Text mt="3" fontSize="10px" color="fg.subtle">
          Start performs cTrader authentication, environment checks,
          reconciliation and data subscription before the bot becomes RUNNING.
        </Text>
      </Panel>
    </Stack>
  );
}
function StrategyState({ bot }: { bot: BotRecord }) {
  const state = (bot as any).runtime?.strategyState;
  if (!state)
    return (
      <Text color="fg.muted" fontSize="xs">
        No strategy event has been persisted yet.
      </Text>
    );
  if (bot.strategy === "xau-multi-edge-portfolio-v1")
    return (
      <Stack gap="3">
        <Grid
          templateColumns={{ base: "1fr 1fr", md: "repeat(3,1fr)" }}
          gap="2"
        >
          <FormBox
            label="Aggregate target"
            value={`${Number(state.lastAggregateTarget ?? 0).toFixed(3)}x`}
          />
          <FormBox
            label="Momentum target"
            value={`${Number(state.momentum?.targetLeverage ?? 0).toFixed(3)}x`}
          />
          <FormBox
            label="Frequency target"
            value={`${Number(state.frequency?.lastTargetLeverage ?? 0).toFixed(3)}x`}
          />
          <FormBox
            label="Last frequency anchor"
            value={state.frequency?.lastEvaluatedAnchor ?? "Not evaluated"}
          />
          <FormBox
            label="Last 1h momentum"
            value={state.frequency?.lastMomentum === undefined ? "Not observed" : `${(Number(state.frequency.lastMomentum) * 100).toFixed(4)}%`}
          />
          <FormBox
            label="Confirmed P3 trend"
            value={
              state.frequency?.p3Trend === 1
                ? "UP"
                : state.frequency?.p3Trend === -1
                  ? "DOWN"
                  : "RANGE"
            }
          />
        </Grid>
        <Text fontSize="10px" color="fg.muted">
          Frequency {state.frequency?.active ? "active" : "flat"} · Last action{" "}
          {state.frequency?.lastAction ?? "not evaluated"} · Decision{" "}
          {state.frequency?.lastDecisionReason ?? "waiting for an anchor"}
        </Text>
      </Stack>
    );
  return (
    <Box
      as="pre"
      whiteSpace="pre-wrap"
      overflowWrap="anywhere"
      fontSize="10px"
      fontFamily="mono"
      color="fg.muted"
    >
      {JSON.stringify(state, null, 2)}
    </Box>
  );
}
function Timeline({
  time,
  title,
  detail,
}: {
  time: ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <Flex gap="3">
      <Box w="96px" flexShrink="0">
        <Box fontSize="10px" fontFamily="mono" color="fg.subtle">
          {time}
        </Box>
      </Box>
      <Box borderLeftWidth="1px" borderColor="border.default" pl="3" pb="2">
        <Text fontSize="xs" fontWeight="700">
          {title}
        </Text>
        <Text fontSize="10px" color="fg.muted">
          {detail}
        </Text>
      </Box>
    </Flex>
  );
}
function NotionalLimit({
  label,
  effective,
  hard,
  multiple,
  mode,
}: {
  label: string;
  effective?: number;
  hard?: number;
  multiple?: number;
  mode: "fixed" | "equity_scaled";
}) {
  const money = (value?: number) => value === undefined
    ? "Unavailable"
    : `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <Box>
      <Flex justify="space-between" gap="3" align="start">
        <Box>
          <Text fontSize="xs" color="fg.muted">{label}</Text>
          <Text mt="1" fontSize="9px" color="fg.subtle">
            Hard cap {money(hard)}
          </Text>
        </Box>
        <Box textAlign="right">
          <Text fontSize="xs" fontFamily="mono" fontWeight="700">
            {money(effective ?? (mode === "fixed" ? hard : undefined))}
          </Text>
          <Text mt="1" fontSize="9px" color={mode === "equity_scaled" ? "status.positive" : "status.warning"} fontWeight="750">
            {mode === "equity_scaled" ? `AUTO · ${multiple?.toFixed(2) ?? "?"}x equity` : "FIXED LEGACY"}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
}
function Limit({
  label,
  value,
  max,
  suffix,
}: {
  label: string;
  value: number;
  max: number;
  suffix: string;
}) {
  const display = (amount: number) => amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return (
    <Box>
      <Flex justify="space-between">
        <Text fontSize="xs" color="fg.muted">
          {label}
        </Text>
        <Text fontSize="xs" fontFamily="mono">
          {display(value)}
          {suffix} / {display(max)}
          {suffix}
        </Text>
      </Flex>
      <Box mt="2">
        <DataBar
          value={value}
          max={max}
          tone={value / max > 0.8 ? "warning" : "positive"}
        />
      </Box>
    </Box>
  );
}
